import {
  FAN_CARD_BACK_BG,
  FAN_CARD_BACK_SURFACE,
  FAN_CARD_GOLD,
  FAN_CARD_GOLD_DARK,
  FAN_CARD_GOLD_LIGHT,
} from "./card-theme";

/** Design-time card size. Layout may grow/shrink; type stays at this scale. */
export const CARD_WIDTH = 300;
/** CSS width/height — always portrait 2:3 */
export const CARD_ASPECT_RATIO = 2 / 3;
export const CARD_ASPECT_RATIO_CSS = "2 / 3";
export const CARD_HEIGHT = Math.round(CARD_WIDTH / CARD_ASPECT_RATIO);

/** Visible card thickness for 3D flip animations */
export const CARD_DEPTH = 12;
export const CARD_BORDER_RADIUS = 18;

/** Expand modal: render larger natively (no CSS upscale) for a crisp look */
export const EXPAND_CARD_MAX_WIDTH = 420;
export const EXPAND_CARD_MIN_WIDTH = 264;

export type CardRenderSize = {
  width: number;
  height: number;
  depth: number;
  halfDepth: number;
  scale: number;
};

export const cardHeightFromWidth = (width: number) => Math.round(width / CARD_ASPECT_RATIO);

/** Type/chrome stay at design size; do not follow responsive card width. */
export const getCardRenderScale = (_size?: Pick<CardRenderSize, "width">) => 1;

export const scaledCardPx = (px: number, scale = 1) => `${Math.round(px * scale)}px`;

export function computeExpandCardLayout(params: {
  availWidth: number;
  availHeight: number;
  maxWidth?: number;
}): CardRenderSize {
  const maxW = params.maxWidth ?? EXPAND_CARD_MAX_WIDTH;
  let width = Math.min(params.availWidth, params.availHeight * CARD_ASPECT_RATIO, maxW);
  width = Math.max(EXPAND_CARD_MIN_WIDTH, width);
  // Snap to 30px steps (same ratio as base 300px card) for clean integer scaling
  width = Math.round(width / 30) * 30;
  width = Math.max(EXPAND_CARD_MIN_WIDTH, Math.min(width, maxW));
  const height = cardHeightFromWidth(width);
  const depthScale = width / CARD_WIDTH;
  const depth = Math.max(8, Math.round(CARD_DEPTH * depthScale));
  const halfDepth = Math.floor(depth / 2);
  return { width, height, depth, halfDepth, scale: 1 };
}

export const cardEdgeBg = FAN_CARD_GOLD;

export const GOLD = FAN_CARD_GOLD;
export const GOLD_LIGHT = FAN_CARD_GOLD_LIGHT;
export const GOLD_DARK = FAN_CARD_GOLD_DARK;
export const CARD_BACK_BG = FAN_CARD_BACK_BG;
export const CARD_BACK_SURFACE = FAN_CARD_BACK_SURFACE;

export const getGoldBorderSx = (scale = 1) => {
  const radius = Math.round(CARD_BORDER_RADIUS * scale);
  const border = Math.max(2, Math.round(3 * scale));
  const pad = Math.max(3, Math.round(5 * scale));
  return {
    border: `${border}px solid ${GOLD}`,
    borderRadius: `${radius}px`,
    boxShadow: `
      inset 0 0 0 1px ${GOLD_LIGHT},
      0 0 0 1px ${GOLD_DARK},
      0 ${Math.round(18 * scale)}px ${Math.round(40 * scale)}px rgba(0, 0, 0, 0.35)
    `,
    backgroundColor: GOLD,
    p: `${pad}px`,
  };
};

export const goldBorderSx = getGoldBorderSx(1);

export const cardBackScrollSx = {
  px: { xs: 1, sm: 2 },
  pb: 1.5,
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: `rgba(200, 164, 75, 0.35) transparent`,
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
    marginBlock: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(200, 164, 75, 0.28)",
    borderRadius: "999px",
    border: `2px solid ${CARD_BACK_BG}`,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "rgba(200, 164, 75, 0.48)",
  },
};

export const formatJoinedDate = (isoDate?: string) => {
  if (!isoDate) return "—";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatMemberSinceYear = (isoDate?: string) => {
  if (!isoDate) return "—";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate.slice(0, 4);
  return String(date.getFullYear());
};

export const reactionEmoji = (type?: string | null) => {
  switch (type?.toLowerCase()) {
    case "love":
      return "❤️";
    case "flower":
      return "🙏";
    case "clap":
      return "👏";
    case "fire":
      return "🔥";
    case "smile":
      return "😊";
    default:
      return "✨";
  }
};
